/**
 * `/embed/card` — the card form, alone, for the mobile app's WebView (MASTER_PLAN M18.2,
 * scope design §4.1 item 5, ADR-034, `Docs/BOOKING_FUNNEL_WIRING.md` "Embedded card
 * capture for the mobile app").
 *
 * accept.blue Hosted Tokenization is an iframe and needs a browser origin, which a native
 * app does not have. So the app opens this route on the tenant's own patient host in a
 * WebView, the patient types the card into the vendor's iframe exactly as on `/payment`,
 * and this page hands the resulting **nonce** to the app, which sends it to the existing
 * `POST /public/drafts/current/payment-method` (or the portal's card route) itself.
 *
 * ## What it renders
 *
 * The tokenizer mount and one button. No app shell, no header, no footer, no funnel CSS
 * (`routes/__root.tsx` renders `/embed/*` bare), because the WebView is a sheet inside the
 * app's own screen and a second header would be a second app.
 *
 * ## What it needs
 *
 * `GET /public/payments/config`, and nothing else: that route is host-scoped and needs no
 * draft cookie (`PublicPaymentsController.config` reads only the tenant), which matters
 * because a WebView has none. It never calls a draft route.
 *
 * ## The messages
 *
 * One JSON string per outcome, to `window.ReactNativeWebView.postMessage` when the app
 * injected it, else to `window.parent.postMessage` with this page's own origin as the
 * target (never `*`), which is what a browser test or an iframe host on the same origin
 * sees:
 *
 * ```ts
 * { type: 'ha.card.nonce', nonce, expiryMonth, expiryYear }   // success
 * { type: 'ha.card.error', message }                          // anything else
 * ```
 *
 * `billingZip` is part of the documented shape but is never sent from here: the hosted
 * form's ZIP is off (as on `/payment`) and the app collects the ZIP natively.
 *
 * ## The nonce
 *
 * Elsewhere the nonce never leaves `useCardForm` (SECURITY.md §8). Here handing it over is
 * the whole job, so the rule becomes: it goes to exactly one receiver, as the one message,
 * and is not stored, logged or put in React state. It is single-use and lives fifteen
 * minutes; the card number itself never reaches this page at all.
 *
 * ## `?theme=`
 *
 * The button's colour, so the sheet matches the app. Only `#rrggbb` (or `rrggbb`) is
 * accepted; anything else is ignored and the tenant's own theme applies. The accessible
 * sibling of the colour is what is painted, so white text on it clears 4.5:1 whatever the
 * app passes (Q-P15).
 *
 * CSP: nothing inline is added — the vendor script is injected by `payments-tokenizer.ts`
 * with the document's nonce exactly as on `/payment`, and a `style` attribute is covered by
 * `style-src 'unsafe-inline'`, which the policy already carries.
 */

import { useEffect, useRef, useState, type CSSProperties } from 'react';

import { accessiblePalette } from '@platform/ui';

import { CARD_ERRORS } from '../lib/api/payments';
import { usePaymentsRuntime } from '../lib/api/payments-client-context';
import type { MountedTokenizer } from '../lib/api/payments-tokenizer';

export const EMBED_CARD_MOUNT = 'ha-embed-card-mount';

export interface CardNonceMessage {
  type: 'ha.card.nonce';
  nonce: string;
  expiryMonth: number;
  expiryYear: number;
  billingZip?: string;
}

export interface CardErrorMessage {
  type: 'ha.card.error';
  message: string;
}

export type EmbedCardMessage = CardNonceMessage | CardErrorMessage;

interface NativeBridge {
  postMessage(message: string): void;
}

/** One message, to the one receiver. */
export function postEmbedMessage(message: EmbedCardMessage): void {
  const json = JSON.stringify(message);
  const native = (window as unknown as { ReactNativeWebView?: NativeBridge }).ReactNativeWebView;
  if (native !== undefined && typeof native.postMessage === 'function') {
    native.postMessage(json);
    return;
  }
  window.parent.postMessage(json, window.location.origin);
}

/** `#rrggbb` or `rrggbb`, lower-cased with the `#`; null for anything else. */
export function parseThemeParam(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const match = /^#?([0-9a-f]{6})$/i.exec(value.trim());
  return match === null ? null : `#${(match[1] as string).toLowerCase()}`;
}

/** The same box as `/payment`, so the vendor's fields look the same in both places. */
const FIELD_STYLES = Object.freeze({
  container: 'font-family: -apple-system, BlinkMacSystemFont, sans-serif; padding: 4px;',
  card: 'border: 1.5px solid #E2E8F0; border-radius: 8px; padding: 12px; font-size: 16px; width: 100%; margin-bottom: 8px;',
  expiryMonth: 'border: 1.5px solid #E2E8F0; border-radius: 8px; padding: 12px; font-size: 16px;',
  expiryYear: 'border: 1.5px solid #E2E8F0; border-radius: 8px; padding: 12px; font-size: 16px;',
  cvv2: 'border: 1.5px solid #E2E8F0; border-radius: 8px; padding: 12px; font-size: 16px;',
  labels: 'font-size: 13px; color: #475569; font-weight: 500; margin-bottom: 4px;',
  labelType: 'static-top',
});

type Status = 'loading' | 'ready' | 'off' | 'error';

export interface CardEmbedProps {
  /** The already-validated `?theme=` colour, or null for the tenant's own. */
  theme: string | null;
}

export function CardEmbed({ theme }: CardEmbedProps) {
  const runtime = usePaymentsRuntime();
  const tokenizer = useRef<MountedTokenizer | null>(null);
  const [status, setStatus] = useState<Status>('loading');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    const abort = new AbortController();

    async function start(): Promise<void> {
      try {
        const config = await runtime.client.config(abort.signal);
        if (!live) return;
        if (!config.enabled || config.scriptUrl === null || config.publicKey === null) {
          setStatus('off');
          setMessage(CARD_ERRORS.skip);
          postEmbedMessage({ type: 'ha.card.error', message: CARD_ERRORS.skip });
          return;
        }
        const mounted = await runtime.mountTokenizer({
          scriptUrl: config.scriptUrl,
          publicKey: config.publicKey,
          target: `#${EMBED_CARD_MOUNT}`,
          styles: FIELD_STYLES,
          showZip: false,
        });
        if (!live) {
          mounted.destroy();
          return;
        }
        tokenizer.current = mounted;
        setStatus('ready');
      } catch {
        if (!live) return;
        setStatus('error');
        setMessage(CARD_ERRORS.retry);
        postEmbedMessage({ type: 'ha.card.error', message: CARD_ERRORS.retry });
      }
    }

    void start();
    return () => {
      live = false;
      abort.abort();
      tokenizer.current?.destroy();
      tokenizer.current = null;
    };
  }, [runtime]);

  const submit = async (): Promise<void> => {
    const mounted = tokenizer.current;
    if (mounted === null) return;
    setSubmitting(true);
    setMessage(null);
    try {
      const card = await mounted.getNonceToken();
      if (card.expiryMonth === null || card.expiryYear === null) {
        throw new Error('no expiry');
      }
      postEmbedMessage({
        type: 'ha.card.nonce',
        nonce: card.nonce,
        expiryMonth: card.expiryMonth,
        expiryYear: card.expiryYear,
      });
    } catch {
      /* The vendor's own wording is for a developer console (rules §23.8). */
      setMessage(CARD_ERRORS.retokenize);
      postEmbedMessage({ type: 'ha.card.error', message: CARD_ERRORS.retokenize });
    } finally {
      setSubmitting(false);
    }
  };

  const palette = theme === null ? null : accessiblePalette(theme);
  const buttonStyle: CSSProperties | undefined =
    palette === null
      ? undefined
      : { backgroundColor: palette.accessible, borderColor: palette.accessible };

  return (
    <main className="mx-auto flex max-w-[540px] flex-col gap-4 p-4">
      <h1 className="sr-only">Card details</h1>
      {/* React owns this box; the vendor owns everything inside it. */}
      <div id={EMBED_CARD_MOUNT} data-testid="card-tokenization-mount" className="min-h-[120px]" />

      {status === 'loading' ? (
        <p className="text-sm text-neutral-500" role="status">
          Loading the secure card form…
        </p>
      ) : null}

      {message === null ? null : (
        <p className="text-sm text-danger-ink" role="alert">
          {message}
        </p>
      )}

      {status === 'ready' ? (
        <button
          type="button"
          data-testid="embed-card-submit"
          disabled={submitting}
          style={buttonStyle}
          onClick={() => {
            void submit();
          }}
          className="h-12 w-full cursor-pointer rounded-input border border-primary-accessible bg-primary-accessible text-base font-semibold text-primary-contrast disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? 'Saving…' : 'Save card'}
        </button>
      ) : null}

      <p className="text-xs text-neutral-500">
        Your card number goes straight to our payment processor. This clinic never sees it.
      </p>
    </main>
  );
}
